// components/popup/AddLowerCourtPopup.tsx
import React, { useState, useEffect } from "react";
import { BasePopup } from "./BasePopup";
import { useTooltip } from "context/TooltipProvider";
import {
  updateAssetAppeal,
  updateRespondentAssetAppeal,
} from "services/assetAppealService";
import { AssetAppeal } from "types";

interface AddLowerCourtPopupProps {
  isOpen: boolean;
  onClose: () => void;
  appealId: string;
  appealType: string;
  existingData?: {
    lower_court_name?: string | null;
    lower_court_type?: string | null;
    lower_court_level?: string | null;
    lower_registration_number?: string | null;
    lower_registration_date?: string | null;
  };
  onSuccess?: React.Dispatch<React.SetStateAction<AssetAppeal | null>>;
  setActiveTab?: React.Dispatch<React.SetStateAction<string>>;
}

export const AddLowerCourtPopup: React.FC<AddLowerCourtPopupProps> = ({
  isOpen,
  onClose,
  appealId,
  existingData,
  onSuccess,
  setActiveTab,
  appealType,
}) => {
  const [formData, setFormData] = useState({
    lower_court_name: "",
    lower_court_type: "",
    lower_court_level: "",
    lower_registration_number: "",
    lower_registration_date: "",
  });

  const [loading, setLoading] = useState(false);
  const { showTooltip } = useTooltip();

  // Pre-fill form with existing data
  useEffect(() => {
    if (existingData && isOpen) {
      setFormData({
        lower_court_name: existingData.lower_court_name || "",
        lower_court_type: existingData.lower_court_type || "",
        lower_court_level: existingData.lower_court_level || "",
        lower_registration_number: existingData.lower_registration_number || "",
        lower_registration_date: existingData.lower_registration_date || "",
      });
    }
  }, [existingData, isOpen]);

  const handleInputChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    if (!formData.lower_court_name) return "Lower Court Name is required.";
    if (!formData.lower_court_type) return "Lower Court Type is required.";
    if (!formData.lower_court_level) return "Lower Court Level is required.";
    if (!formData.lower_registration_number)
      return "Registration Number is required.";
    if (!formData.lower_registration_date)
      return "Registration Date is required.";
    return "";
  };

  const handleClose = () => {
    // Reset form when closing
    setFormData({
      lower_court_name: "",
      lower_court_type: "",
      lower_court_level: "",
      lower_registration_number: "",
      lower_registration_date: "",
    });
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate form
    const validationError = validateForm();
    if (validationError) {
      showTooltip(validationError, "error");
      return;
    }

    try {
      setLoading(true);

      // Create FormData for the request
      const submitFormData = new FormData();

      // Append all form fields
      Object.keys(formData).forEach((key) => {
        if (formData[key as keyof typeof formData]) {
          submitFormData.append(key, formData[key as keyof typeof formData]);
        }
      });
      let response;
      // Call API to update appeal with lower court details
      if (appealType === "appeal") {
        response = await updateAssetAppeal(appealId, submitFormData);
      } else {
        response = await updateRespondentAssetAppeal(appealId, submitFormData);
      }

      if (response) {
        // Show success message
        showTooltip("Lower court details added successfully!", "success");

        // Reset form and close popup

        handleClose();
      }

      // Call success callback if provided
      if (onSuccess) {
        if (appealType === "appeal") {
          onSuccess(response?.appeal);
        } else {
          onSuccess(response);
        }
        setActiveTab?.("Appeal");
      }
    } catch (error: any) {
      console.error("Error adding lower court details:", error);
      const errorMessage =
        error.response?.data?.message || "Failed to add lower court details";
      showTooltip(errorMessage, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <BasePopup
      isOpen={isOpen}
      onClose={handleClose}
      title="Add Lower Court Details"
      description="Add lower court information for the appeal"
    >
      <form onSubmit={handleSubmit}>
        {/* Lower Court Name */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Lower Court Name</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="lower_court_name"
            placeholder="Enter lower court name"
            value={formData.lower_court_name}
          />
        </div>

        {/* Lower Court Type */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Lower Court Type</label>
          <input
            className="referral-form-field"
            onChange={handleInputChange}
            name="lower_court_type"
            value={formData.lower_court_type}
            placeholder="Enter court type"
          />
        </div>

        {/* Lower Court Level */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Lower Court Level</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="lower_court_level"
            value={formData.lower_court_level}
            placeholder="Enter court level"
          />
        </div>

        {/* Registration Number */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Registration Number</label>
          <input
            type="text"
            className="referral-form-field"
            onChange={handleInputChange}
            name="lower_registration_number"
            placeholder="Enter registration number"
            value={formData.lower_registration_number}
          />
        </div>

        {/* Registration Date */}
        <div className="flex flex-col mt-4">
          <label className="referral-popup-label">Registration Date</label>
          <input
            type="date"
            className="referral-form-field"
            onChange={handleInputChange}
            name="lower_registration_date"
            value={formData.lower_registration_date}
          />
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={loading}
          className={`mt-6 mb-4 w-full referral-form-button ${
            loading ? "opacity-50 cursor-not-allowed" : "hover:bg-gray-800"
          }`}
        >
          {loading ? "Submitting..." : "Add Lower Court Details"}
        </button>
      </form>
    </BasePopup>
  );
};
